import { Suspense } from 'react';
import { type SubmitHandler, useForm } from 'react-hook-form';

import type * as api from '@gitbook/api-client';
import {
    BooleanField,
    Description,
    DialogBody,
    DialogFooter,
    Fieldset,
    Form,
    Group,
    Label,
    LoadingPanel,
    Submit,
    Switch,
    useDialogClose,
} from '@gitbook/spine-react';
import { assert } from '@gitbook/util-ts';

import { OpenAPISpecPicker } from '../../OpenAPI';

interface ConfigureOpenAPIContentSourceFormInputs {
    models: boolean;
    downloadLink?: boolean;
    specSlug: string | null;
}

export function ContentSourceOpenAPIForm(props: {
    organizationId: string;
    defaultValues: ConfigureOpenAPIContentSourceFormInputs;
    buttonLabel: string;
    onSubmit: (source: api.ComputedContentSourceRevisionOpenAPI) => void;
}) {
    const { buttonLabel, defaultValues, organizationId } = props;
    const form = useForm<ConfigureOpenAPIContentSourceFormInputs>({
        defaultValues,
    });
    const specSlug = form.watch('specSlug');
    const closeDialog = useDialogClose();
    const onSubmit: SubmitHandler<ConfigureOpenAPIContentSourceFormInputs> = (data) => {
        assert(data.specSlug);
        props.onSubmit({
            type: 'builtin:openapi',
            props: { models: data.models, downloadLink: data.downloadLink },
            dependencies: {
                spec: {
                    ref: {
                        kind: 'openapi',
                        spec: data.specSlug,
                    },
                },
            },
        });
        closeDialog();
    };

    return (
        <Form form={form} onSubmit={form.handleSubmit(onSubmit)}>
            <DialogBody>
                <Fieldset>
                    <Suspense fallback={<LoadingPanel size="xlarge" />}>
                        <OpenAPISpecPicker
                            form={form}
                            organizationId={organizationId}
                            name="specSlug"
                            analyticsContext={{
                                triggerLocation: 'edit-content-source-dialog',
                            }}
                        />
                    </Suspense>
                    {specSlug && (
                        <>
                            <BooleanField name="models">
                                <Group>
                                    <Label>Include model reference page</Label>
                                    <Description>
                                        Add a dedicated page listing all schemas defined in the
                                        OpenAPI spec.
                                    </Description>
                                </Group>
                                <Switch />
                            </BooleanField>
                            <BooleanField name="downloadLink">
                                <Group>
                                    <Label>Include download link</Label>
                                    <Description>
                                        Add a link to allow visitors to download the OpenAPI spec.
                                    </Description>
                                </Group>
                                <Switch />
                            </BooleanField>
                        </>
                    )}
                </Fieldset>
            </DialogBody>
            <DialogFooter>
                <Submit label={buttonLabel} disabled={!specSlug} />
            </DialogFooter>
        </Form>
    );
}
